import { ProfileTransformResponse } from "../../../../services/Profile/profileAPI.types";
import { AdditionalInformationLabelKey, KeyDatesLabelKey } from "../../shared/types";
import { KeyInformationSponsorRowProps } from "../Sponsor/Sponsor";
import type { PathToBronzeOverviewResponse } from "../../../../services/BronzeIncentives/bronzeIncentivesAPI.types";
import type { BronzeOverviewResponse } from "../../../../services/BronzeIncentives/bronzeIncentivesAPI.types";
import type { SsiOverviewResponse } from "../../../../services/StrongStart/ssiAPI.types";
import { QUALIFICATION_STATUS } from "../../../../common/enums/incentive-enums";
import { ANA_MARKETS } from "../../../../common/constants/market-constants";
import type { CorePlusIncentiveLabelKey } from "../CorePlusIncentiveStatus/CorePlusIncentiveStatus";
type AwardInfo = NonNullable<ProfileTransformResponse["pinAwardSummary"]>;
type AboInfo = NonNullable<ProfileTransformResponse["aboInfo"]>;
type OrderInfo = NonNullable<ProfileTransformResponse["orderInfo"]>;
type AdditionalRegInfo = NonNullable<ProfileTransformResponse["additionalRegInfo"]>;

const CURRENT_BUSINESS_EFFECTIVE_DATE_LABEL: KeyDatesLabelKey = "currentBusinessEffectiveDate";
const BUSINESS_ANNIVERSARY_DATE_LABEL: KeyDatesLabelKey = "businessAnniversaryDate";

export const transformAwardInfo = (awardInfo?: AwardInfo) => {
  return [
    {
      label: "previousAward",
      pin: awardInfo?.previousPYPin?.code,
      level: awardInfo?.previousPYPin?.name,
      date: awardInfo?.previousPYPin?.qualPeriod,
      up: "",
      isValid: true,
    },
    {
      label: "currentAward",
      pin: awardInfo?.currentPin?.code,
      level: awardInfo?.currentPin?.name,
      date: awardInfo?.currentPin?.qualPeriod,
      up: awardInfo?.currentPin?.trend,
      isValid: awardInfo?.currentPin ? true : false,
    },
    {
      label: "trackingAward",
      pin: awardInfo?.trackingPin?.code,
      level: awardInfo?.trackingPin?.name,
      date: awardInfo?.trackingPin?.qualPeriod,
      up: awardInfo?.trackingPin?.trend,
      name: awardInfo?.trackingPin?.name,
      isValid: awardInfo?.trackingPin ? true : false,
    },
    {
      label: "highestAward",
      pin: awardInfo?.highestPin?.code,
      level: awardInfo?.highestPin?.name,
      date: awardInfo?.highestPin?.qualPeriod,
      up: "",
      isValid: true,
    },
  ];
};

export const transformKeyDates = (
  aboInfo?: AboInfo,
  orderInfo?: OrderInfo,
  showRenewalDate = true,
  showBirthday = true,
): { label: KeyDatesLabelKey; value?: string }[] => {
  const { entryDate = "", currentBusinessNatureEffectiveDate = "" } = aboInfo || {};
  const shouldDisplayCurrentBED =
    entryDate.length > 0 &&
    currentBusinessNatureEffectiveDate.length > 0 &&
    entryDate !== currentBusinessNatureEffectiveDate;

  const currentBusinessEffectiveDate = shouldDisplayCurrentBED && {
    label: ANA_MARKETS.includes(aboInfo?.countryCode ?? "")
      ? BUSINESS_ANNIVERSARY_DATE_LABEL
      : CURRENT_BUSINESS_EFFECTIVE_DATE_LABEL,
    value: currentBusinessNatureEffectiveDate,
  };

  const hideBirthday = !showBirthday && aboInfo?.privacyFlag;
  return [
    !hideBirthday && {
      label: "birthday",
      value: aboInfo?.isoBirthdate,
    },
    showRenewalDate && {
      label: "renewal",
      value: aboInfo?.renewalDate,
    },
    currentBusinessEffectiveDate,
    {
      label: "entry",
      value: entryDate,
    },
    {
      label: "recentSponsoring",
      value: aboInfo?.recentSponsorDate,
    },
    {
      label: "recentOrder",
      value: orderInfo?.aboRecentOrderDate,
    },
  ].filter(Boolean) as { label: KeyDatesLabelKey; value?: string }[];
};

export const transformAdditionalRegInfo = (
  additionalRegInfo?: AdditionalRegInfo,
): { label: AdditionalInformationLabelKey; value?: boolean }[] => {
  return [
    {
      label: "renewalConsent",
      value: additionalRegInfo?.renewalConsent,
    },
    {
      label: "orderBlock",
      value: additionalRegInfo?.orderBlock,
    },
    {
      label: "sponsorBlock",
      value: additionalRegInfo?.sponsorBlock,
    },
    {
      label: "autoRenewal",
      value: additionalRegInfo?.autoRenewal,
    },
    {
      label: "contractSigned",
      value: additionalRegInfo?.contractSigned,
    },
  ].filter(Boolean) as { label: AdditionalInformationLabelKey; value?: boolean }[];
};

export const transformSponsor = (aboInfo?: AboInfo): KeyInformationSponsorRowProps[] => {
  const sponsorIsPlatinum = aboInfo?.sponsor.isPlatinum ?? false;
  const fosterIsPlatinum = aboInfo?.fosterSponsor.isPlatinum ?? false;

  const isSponsor =
    (aboInfo?.sponsor.isValid ?? false) && !sponsorIsPlatinum && !(aboInfo?.fosterSponsor.isValid ?? false);
  const isSponsorAndPlatinum = (aboInfo?.sponsor.isValid ?? false) && sponsorIsPlatinum && !fosterIsPlatinum;

  const sponsor: KeyInformationSponsorRowProps = {
    type: isSponsorAndPlatinum ? "sponsorPlatinum" : "sponsor",
    affNo: aboInfo?.sponsor.affNo ?? "",
    aboNo: aboInfo?.sponsor.aboNo ?? "",
    isValid: isSponsor || isSponsorAndPlatinum,
    displayName: aboInfo?.sponsor.displayName,
    localName: aboInfo?.sponsor.nameLocal,
    name: aboInfo?.sponsor.name ?? "",
    phone: aboInfo?.sponsor.phone,
    email: aboInfo?.sponsor.email,
    privacyFlag: aboInfo?.sponsor.privacyFlag,
    address: aboInfo?.sponsor.address,
    imageRefUrl: aboInfo?.sponsor.imageRefUrl,
  };

  const foster: KeyInformationSponsorRowProps = {
    type: fosterIsPlatinum ? "fosterSponsorPlatinum" : "fosterSponsor",
    affNo: aboInfo?.fosterSponsor.affNo ?? "",
    aboNo: aboInfo?.fosterSponsor.aboNo ?? "",
    isValid: aboInfo?.fosterSponsor.isValid ?? false,
    displayName: aboInfo?.fosterSponsor.displayName,
    localName: aboInfo?.fosterSponsor.nameLocal,
    name: aboInfo?.fosterSponsor.name ?? "",
    phone: aboInfo?.fosterSponsor.phone,
    email: aboInfo?.fosterSponsor.email,
    privacyFlag: aboInfo?.fosterSponsor.privacyFlag,
    address: aboInfo?.fosterSponsor.address,
    imageRefUrl: aboInfo?.fosterSponsor.imageRefUrl,
  };

  const international: KeyInformationSponsorRowProps = {
    type: "internationalSponsor",
    affNo: aboInfo?.internationalSponsor.affNo ?? "",
    aboNo: aboInfo?.internationalSponsor.aboNo ?? "",
    isValid: aboInfo?.internationalSponsor.isValid ?? false,
    displayName: aboInfo?.internationalSponsor.displayName,
    localName: aboInfo?.internationalSponsor.nameLocal,
    name: aboInfo?.internationalSponsor.name ?? "",
    phone: aboInfo?.internationalSponsor.phone,
    email: aboInfo?.internationalSponsor.email,
    privacyFlag: aboInfo?.internationalSponsor.privacyFlag,
    address: aboInfo?.internationalSponsor.address,
    imageRefUrl: aboInfo?.internationalSponsor.imageRefUrl,
  };

  const platinum: KeyInformationSponsorRowProps = {
    type: "platinum",
    affNo: aboInfo?.platinumSponsor.affNo ?? "",
    aboNo: aboInfo?.platinumSponsor.aboNo ?? "",
    isValid: (aboInfo?.platinumSponsor.isValid ?? false) && !sponsorIsPlatinum && !fosterIsPlatinum,
    displayName: aboInfo?.platinumSponsor.displayName,
    localName: aboInfo?.platinumSponsor.nameLocal,
    name: aboInfo?.platinumSponsor.name ?? "",
    phone: aboInfo?.platinumSponsor.phone,
    email: aboInfo?.platinumSponsor.email,
    privacyFlag: aboInfo?.platinumSponsor.privacyFlag,
    address: aboInfo?.platinumSponsor.address,
    imageRefUrl: aboInfo?.platinumSponsor.imageRefUrl,
  };

  return [sponsor, foster, international, platinum];
};

export const transformCorePlusIncentive = (
  ptbData?: PathToBronzeOverviewResponse,
  bfiData?: BronzeOverviewResponse,
  bbiData?: BronzeOverviewResponse,
  ssiData?: SsiOverviewResponse,
): { label: CorePlusIncentiveLabelKey; graduated?: boolean; notStarted?: boolean }[] => {
  return [
    {
      label: "pathToBronze",
      graduated: ptbData?.graduated,
      notStarted: !ptbData?.eligible && !ptbData?.earning,
    },
    {
      label: "bronzeFoundation",
      graduated: bfiData?.eligibility.graduated,
      notStarted: bfiData?.eligibility.notStarted,
    },
    {
      label: "bronzeBuilder",
      graduated: bbiData?.eligibility.graduated,
      notStarted: bbiData?.eligibility.notStarted,
    },
    {
      label: "ssi",
      graduated: ssiData?.IncomeData.Status === QUALIFICATION_STATUS.GRADUATED,
      notStarted: !ssiData?.IncomeData.Status || ssiData?.IncomeData.Status === QUALIFICATION_STATUS.NOT_ELIGIBLE,
    },
  ];
};
